import api from '@pray/shared/utils/api';

import type { MarketingCampaignBudgetType } from './getAllMarketingCampaigns';

export type DeriveMarketingCampaignBudgetParams = {
  artistId: string;
  budgetType: MarketingCampaignBudgetType;
  serviceFeePercent: number;
  startDate: string;
  endDate?: string | null;
  // The campaign's budget as invoiced: RETAIL, fee included.
  retailBudgetCents?: number | null;
  dailyBudgetCents?: number | null;
  // Ongoing deals are agreed per month; the server returns the daily pace so the browser never
  // has to divide.
  monthlyBudgetCents?: number | null;
  // Set when previewing an edit, so the campaign being edited is not reported as its own conflict.
  excludeCampaignId?: string | null;
};

/**
 * Asks the server what a proposed term would cost. The client performs no fee arithmetic: the
 * figures rendered in the form preview are the same ones the create/update commands will persist,
 * because both come from the server's MarketingCampaignFeeMath.
 */
export const deriveMarketingCampaignBudget = async ({
  artistId,
  budgetType,
  serviceFeePercent,
  startDate,
  endDate,
  retailBudgetCents,
  dailyBudgetCents,
  monthlyBudgetCents,
  excludeCampaignId,
}: DeriveMarketingCampaignBudgetParams) => {
  const url = `/web/studio/artists/${artistId}/marketing-campaigns/derive-budget`;

  const body: Record<string, unknown> = {
    budget_type: budgetType,
    service_fee_percent: serviceFeePercent,
    start_date: startDate,
  };

  // Explicit null/undefined checks rather than truthiness: a 0 is not a value to silently drop,
  // and the server rejects it far more usefully than a preview built from a missing figure would.
  if (endDate) body.end_date = endDate;
  if (retailBudgetCents != null) body.retail_budget_cents = retailBudgetCents;
  if (dailyBudgetCents != null) body.daily_budget_cents = dailyBudgetCents;
  if (monthlyBudgetCents != null) body.monthly_budget_cents = monthlyBudgetCents;
  if (excludeCampaignId) body.exclude_campaign_id = excludeCampaignId;

  const response = await api.executePostRequest(url, body);

  return response.data;
};
